import dayjs from 'dayjs'
import bytes from 'bytes'
import relativeTime from 'dayjs/plugin/relativeTime'
import updateLocale from 'dayjs/plugin/updateLocale'
import 'dayjs/locale/zh-cn'
import { Address4, Address6 } from 'ip-address'
import { Router as RouterConstant } from '@core/Constants'
import { getBaseURL } from '@core/EnvUtils'
import type { AnyFn, Fn } from '@vueuse/core'
import { useIntervalFn } from '@vueuse/core'
import type { Ref } from 'vue'
import Cookies from 'js-cookie'

dayjs.extend(relativeTime)
dayjs.extend(updateLocale)
dayjs.locale('zh-CN')

const HEYTAP_BROWSER = 'HeyTapBrowser'

export function isEmpty(str: string | undefined): str is undefined {
  return str == undefined || str.trim() == ''
}

export function isObject(data: unknown): boolean {
  return data !== null && !Array.isArray(data) && typeof data === typeof {}
}

dayjs.updateLocale('zh-CN', {
  relativeTime: {
    future: '%s内',
    past: '%s前',
    s: '几秒',
    ss: '%d 秒',
    m: '1 分钟',
    mm: '%d 分钟',
    h: '1 小时',
    hh: '%d 小时',
    d: '1 天',
    dd: '%d 天',
    M: '1 个月',
    MM: '%d 个月',
    y: '1 年',
    yy: '%d 年',
  },
})

export const toElapseTime = (timestamp: number) => {
  const date = new Date(timestamp)
  const fromTime = dayjs(date).fromNow()

  return fromTime
}

interface Params {
  [option: string]: any
}

export function encodeRelativePathWithParams(urlString: string, params: Params = {}) {
  const tempUrl = 'https://test.com/'

  const path = new URL(urlString, tempUrl)
  const { searchParams } = path

  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key]

      searchParams.append(key, value)
    }
  }

  return path.href.replace(tempUrl, '')
}

export class URLBuilder {
  #url: URL
  constructor(origin: string) {
    origin = this.#formatPath(origin)

    this.#url = new URL(origin)
  }

  #formatPath(path: string): string {
    path = path.trim()
    path = path.startsWith('.') ? path.slice(1) : path
    path = path.endsWith('/') ? path.slice(0, path.length - 1) : path

    return path
  }

  appendPath(subPath: string) {
    subPath = this.#formatPath(subPath)

    if (this.#url) {
      this.#url.pathname = this.#formatPath(this.#url.pathname) + (subPath.startsWith('/') ? subPath : '/' + subPath)
    }

    return this
  }

  appendQuery(params: Params = {}) {
    const { searchParams } = this.#url

    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key]

        searchParams.append(key, value)
      }
    }

    return this
  }

  toString(): string {
    return this.#url.toString()
  }
}

export function formatBytes(data: number): string {
  return bytes.format(data, { unitSeparator: ' ' })
}

export function generateLanUrl(host: string, port: number, useHttps = true): URL | undefined {
  if (isEmpty(host)) return undefined
  if (port <= 0) return undefined

  //TODO:此阶段由于host没有包含port信息，默认host中存在‘:’符号即为IPV6格式
  let hostStr = host

  let address: Address4 | Address6 | undefined = undefined

  try {
    if (host.indexOf(':') >= 0) {
      hostStr = `[${hostStr}]`
      address = new Address6(host)
    } else {
      address = new Address4(host)
    }
  } catch (error) {
    return undefined
  }

  return address.isCorrect() ? new URL(`${useHttps ? 'https' : 'http'}://${hostStr}:${port}`) : undefined
}

export function saveLinkAsFile(fileUrl: string, fileName: string) {
  console.log(`[Transmission]: saveLinkAsFile ${fileName} start ${new Date().toLocaleString()}`, fileUrl)

  const downloadLink = document.createElement('a')

  downloadLink.href = fileUrl
  downloadLink.download = fileName
  downloadLink.addEventListener('error', (e: ErrorEvent) => {
    console.log(`[Transmission]: saveLinkAsFile ${fileName} failed ${new Date().toLocaleString()}`, e)
  })
  downloadLink.click()
  downloadLink.remove()
  setTimeout(() => {
    URL.revokeObjectURL(fileUrl)
  }, 10000)
}

export function isHeyTapBrowser() {
  const reg = new RegExp(HEYTAP_BROWSER)

  return reg.test(navigator.userAgent)
}

/**
 *  获取 第一个/baseURL/mapp/ 与 ？或 / 之间的字符串
 *
 * @param url - 目标url字符串
 * @param baseURL - 基准路径
 * @returns  appid
 */
export function getAppIdByPath(url: string, baseURL: string = getBaseURL()) {
  const baseUrl = `${baseURL}${RouterConstant.MICRO_APP_MAIN}`
  const previewBaseUrl = `${baseURL}${RouterConstant.MICRO_APP_PREVIEW_BASE}`
  const reg = new RegExp(`(${baseUrl}|${previewBaseUrl})+\/([^\/?]*)`)

  return url.match(reg)?.[2]
}

export function isRelativeUrl(url: string): boolean {
  try {
    new URL(url)

    return false
  } catch (error) {
    return true
  }
}

export function changeDocumentPageTitle(name: string) {
  window.document.title = name
}

export function isMac() {
  const userAgent = navigator.userAgent.toLowerCase()
  const macosPlatforms = /(macintosh|macintel|macppc|mac68k|macos)/i

  return (navigator.platform?.toUpperCase()?.indexOf('MAC') >= 0 || macosPlatforms.test(userAgent)) && !isIpadOS()
}

// 由于19款ipad pro支持了桌面模式,会被检测为桌面端，所以暂时使用触摸方式单独判断
function isIpadOS() {
  const isIpad = navigator.userAgent.toLowerCase().indexOf('ipad') !== -1

  if (!isIpad && navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2) {
    return true
  }

  return isIpad
}

// 由于工具库涉及window对象，在纯js文件中需要异步引入调用
export async function isDesktop() {
  const device = (await import('current-device')).default

  return device.desktop() && !isIpadOS()
}

export async function isMobileTerminal() {
  const device = (await import('current-device')).default

  return device.mobile() || device.tablet() || isIpadOS() || isHeyTapBrowser()
}

class TaskPool {
  private taskQueue: AnyFn[] = []
  private resume: Fn
  private isActive: Readonly<Ref<boolean>>
  constructor(interval: number) {
    const { pause, resume, isActive } = useIntervalFn(
      () => {
        const taskFunction = this.taskQueue.shift()

        try {
          taskFunction ? taskFunction() : pause()
        } catch (error) {
          console.log('[useIntervalFn]: invoke failed', taskFunction, error)
        }
      },
      interval,
      {
        immediate: false,
        immediateCallback: true,
      }
    )

    this.resume = resume
    this.isActive = isActive
  }

  addTask(taskFunction: AnyFn) {
    this.taskQueue.push(taskFunction)
    !this.isActive.value && this.resume()
  }
}

const defaultTaskPool = new TaskPool(2000)

export const transmissionTaskPool = defaultTaskPool

/**
 * 每次可以获取100以内的使用次数最少的数字
 */
export class TagTracker {
  tags: Map<string, number>

  constructor(numberFlag: number) {
    this.tags = new Map<string, number>()

    for (let i = 1; i <= numberFlag; i++) {
      this.tags.set(String(i).padStart(2, '0'), 0)
    }
  }

  takeTag(): string {
    // 找到使用次数最少的 tag
    let minCount = Infinity
    let minCountTags: string[] = []

    for (const [tag, count] of this.tags) {
      if (count < minCount) {
        minCount = count
        minCountTags = [tag]
      } else if (count === minCount) {
        minCountTags.push(tag)
      }
    }

    // 随机选择一个使用次数最少的 tag
    const randomIndex = Math.floor(Math.random() * minCountTags.length)
    const selectedTag = minCountTags[randomIndex]

    this.tags.set(selectedTag, this.tags.get(selectedTag)! + 1)

    return selectedTag
  }

  returnTag(tag: string) {
    const usageCount = this.tags.get(tag) ?? 0

    usageCount > 0 && this.tags.set(tag, usageCount - 1)
  }

  getUsageCount(tag: string): number | undefined {
    return this.tags.get(tag)
  }
}

const tagTracker = new TagTracker(10)

export const transmissionTagTracker = tagTracker

export const isInIosAPP = () => Cookies.get('OWORK_FROM') === 'ios'
